"use client";
import {
  ArrowUpCircle,
  CheckCircle,
  ChevronDown,
  Clock,
  Coins,
  DollarSign,
  FileX,
  Gift,
  Package,
  ShoppingCart,
  Tag,
  Target,
  TrendingDown,
  TrendingUp,
  Users,
  Workflow,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { AnimatedBeamMultiple } from "../animated-beam-multiple";
import { CreditModal } from "../credit-modal";
import {ContactSalesModal} from "../enterprise-form-modal";
import "./pricing.css";

const Pricing = () => {
  const [selectedPlan, setSelectedPlan] = useState("standard");
  const [_expandedPricing, setExpandedPricing] = useState({
    payAsYouGo: false,
    subscription: false,
    enterprise: false,
  });

  const [isCreditsModalOpen, setIsCreditsModalOpen] = useState(false);
  const [isContactSalesModalOpen, setIsContactSalesModalOpen] = useState(false);

  const PricingTierFeature = ({ icon, copy }) => {
    return (
      <div className="r2-pricing__feature">
        <div className="r2-pricing__feature-icon">{icon}</div>
        <span className="r2-pricing__feature-text">{copy}</span>
      </div>
    );
  };

  return (
    <section className="r2-pricing">
      <div className="r2-pricing__backdrop" />

      <div className="r2-pricing__container">
        <div className="r2-pricing__intro">
          <h2 className="r2-pricing__title">Simple, transparent pricing</h2>
          <p className="r2-pricing__subtitle">
            Choose the plan that fits your rental business today-with room to
            grow.
          </p>

          <button
            onClick={() => setIsCreditsModalOpen(true)}
            style={{ unset: "all" }}
            className="r2-pricing__credits-button"
          >
            <span className="r2-pricing__credits-text">
              View credit pricing
            </span>
            <ChevronDown className="r2-pricing__credits-icon" />
          </button>
        </div>

        <div className="r2-pricing__spacer"></div>

        <div className="r2-pricing__grid">
          {/* Pay-As-You-Go Card */}
          <div className="r2-pricing__card r2-pricing__card--paygo">
            <div className="r2-pricing__card-head">
              <div className="r2-pricing__card-ribbon">
                <p className="r2-pricing__card-ribbon-text">FLEXIBLE</p>
              </div>

              <h3 className="r2-pricing__card-title">Pay-As-You-Go</h3>
              <p className="r2-pricing__card-subtitle">
                Only pay when you need it.
              </p>

              <div className="r2-pricing__price-block">
                <div className="r2-pricing__price-row">
                  <span className="r2-pricing__price">$2.99</span>
                  <span className="r2-pricing__price-unit">/credit </span>
                </div>
                <p className="r2-pricing__price-note r2-pricing__price-note--blue">
                  {"get 10% off your first 30 days\n\n"}
                </p>
              </div>

              <div>
                <button
                  type="button"
                  className="r2-pricing__cta-button r2-pricing__cta-button--full"
                  onClick={() => {
                    window.open(
                      "https://rent-id.io/sign-in",
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }}
                >
                  Get Started
                </button>
                <p className="r2-pricing__fineprint-note">
                  Additional tax and state fees may apply
                </p>
              </div>
            </div>

            <div className="r2-pricing__card-body">
              <div className="r2-pricing__feature-list">
                <PricingTierFeature
                  icon={
                    <TrendingUp className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Buy credits as you scale your business"
                />
                <PricingTierFeature
                  icon={
                    <TrendingDown className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Get 10% off your first 30 days"
                />
                <PricingTierFeature
                  icon={
                    <Package className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Mix and match services"
                />
                <PricingTierFeature
                  icon={
                    <Workflow className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Build custom workflows"
                />
                <PricingTierFeature
                  icon={
                    <Zap className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Results delivered in 10 seconds or less"
                />
                <PricingTierFeature
                  icon={
                    <FileX className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="No annual contract or commitments"
                />
                <PricingTierFeature
                  icon={
                    <Target className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Upgrade to Subscription at any time"
                />
                <PricingTierFeature
                  icon={
                    <ShoppingCart className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Purchase Bulk Credits at any time"
                />
              </div>
            </div>
          </div>

          {/* Bundles Card */}
          <div className="r2-pricing__card r2-pricing__card--bundles">
            <div className="r2-pricing__card-head">
              <div className="r2-pricing__card-ribbon r2-pricing__card-ribbon--solid">
                <p className="r2-pricing__card-ribbon-text">VOLUME DISCOUNT</p>
              </div>

              <h3 className="r2-pricing__card-title">Bulk Credits</h3>
              <p className="r2-pricing__card-subtitle">Mix, match, and save.</p>

              <div className="r2-pricing__price-block">
                <div className="r2-pricing__price-row">
                  <span className="r2-pricing__price">$2.69</span>
                  <span className="r2-pricing__price-unit">/credit</span>
                </div>
                <p className="r2-pricing__price-note r2-pricing__price-note--blue">
                  10% off services • 10 credit min order{" "}
                </p>
              </div>

              <div>
                <div className="r2-pricing__coming-soon">
                  <span className="r2-pricing__coming-soon-text">
                    Coming Soon
                  </span>
                </div>
                <p className="r2-pricing__fineprint-note">
                  Additional tax and state fees may apply
                </p>
              </div>
            </div>

            <div className="r2-pricing__card-body">
              <div className="r2-pricing__feature-list">
                <PricingTierFeature
                  icon={
                    <ShoppingCart className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Purchase Bulk Credits at any time"
                />
                <PricingTierFeature
                  icon={
                    <TrendingDown className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Save 10% off every credit"
                />
                <PricingTierFeature
                  icon={
                    <ShoppingCart className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Minimum order quantity of 10"
                />
                <PricingTierFeature
                  icon={
                    <Package className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Apply credits to any services"
                />
                <PricingTierFeature
                  icon={
                    <Clock className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Credits expire 1 year after purchase"
                />
                <PricingTierFeature
                  icon={
                    <Zap className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Results delivered in 10 seconds or less"
                />
                <PricingTierFeature
                  icon={
                    <TrendingUp className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Great for growing fleets and seasonal surges"
                />
                <PricingTierFeature
                  icon={
                    <CheckCircle className="r2-pricing__icon r2-pricing__icon--sky" />
                  }
                  copy="Simplified billing with one upfront purchase"
                />
              </div>
            </div>
          </div>

          {/* Subscription Plan Card */}
          <div className="r2-pricing__card r2-pricing__card--subscription">
            {/* White Subscription card with tabs inside */}
            <div className="r2-pricing__subscription-wrap">
              <div
                className="r2-pricing__subscription-card"
                style={{
                  boxShadow:
                    selectedPlan === "standard"
                      ? "0 4px 8px -3px rgba(129, 140, 248, 0.9), 0 4px 6px -4px rgba(129, 140, 248, 0.9)"
                      : "0 4px 8px -3px rgba(244, 114, 182, 0.9), 0 4px 6px -4px rgba(244, 114, 182, 0.9)",
                }}
              >
                <div className="r2-pricing__card-head">
                  <div className="r2-pricing__card-ribbon r2-pricing__card-ribbon--solid r2-pricing__card-ribbon--tabs">
                    <div className="r2-pricing__tabs">
                      <button
                        onClick={() => setSelectedPlan("basic")}
                        className={`r2-pricing__tab ${
                          selectedPlan === "basic"
                            ? "r2-pricing__tab--active r2-pricing__tab--basic"
                            : "r2-pricing__tab--inactive"
                        }`}
                        style={{
                          boxShadow:
                            selectedPlan === "basic"
                              ? "0 4px 8px -3px rgba(244, 114, 182, 0.9), 0 4px 6px -4px rgba(244, 114, 182, 0.9)"
                              : undefined,
                        }}
                      >
                        Basic Plan
                      </button>
                      <button
                        onClick={() => setSelectedPlan("standard")}
                        className={`r2-pricing__tab ${
                          selectedPlan === "standard"
                            ? "r2-pricing__tab--active r2-pricing__tab--standard"
                            : "r2-pricing__tab--inactive"
                        }`}
                        style={{
                          boxShadow:
                            selectedPlan === "standard"
                              ? "0 4px 8px -3px rgba(129, 140, 248, 0.9), 0 4px 6px -4px rgba(129, 140, 248, 0.9)"
                              : undefined,
                        }}
                      >
                        Standard Plan
                      </button>
                    </div>
                  </div>
                  <h3 className="r2-pricing__card-title">Subscription</h3>
                  <p className="r2-pricing__card-subtitle">
                    {"Monthly billing, serious value."}
                  </p>

                  <div className="r2-pricing__price-block">
                    <div className="r2-pricing__price-row">
                      <span className="r2-pricing__price r2-pricing__price--clickable">
                        {selectedPlan === "basic" ? "$29.99" : "$59.99"}
                      </span>
                      <span className="r2-pricing__price-unit">/month</span>
                    </div>
                    <p
                      className={`r2-pricing__price-note r2-pricing__price-note--center ${
                        selectedPlan === "basic"
                          ? "r2-pricing__price-note--pink"
                          : "r2-pricing__price-note--indigo"
                      }`}
                    >
                      {selectedPlan === "basic"
                        ? "15% off PAYGO •  2 FREE credits"
                        : "20% off PAYGO •  5 FREE credits"}
                    </p>
                  </div>
                  <div className="r2-pricing__subscription-footer">
                    <div className="r2-pricing__coming-soon">
                      <span className="r2-pricing__coming-soon-text">
                        Coming Soon
                      </span>
                    </div>
                    <p className="r2-pricing__bundle-note">
                      <Tag
                        className={`r2-pricing__bundle-icon ${
                          selectedPlan === "standard"
                            ? "r2-pricing__bundle-icon--indigo"
                            : "r2-pricing__bundle-icon--pink"
                        }`}
                      />
                      <span
                        className={`r2-pricing__bundle-text ${
                          selectedPlan === "standard"
                            ? "r2-pricing__bundle-text--indigo"
                            : "r2-pricing__bundle-text--pink"
                        }`}
                      >
                        {selectedPlan === "basic"
                          ? "Bonus: 15% off all bundles"
                          : "Bonus: 20% off all bundles"}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="r2-pricing__card-body">
              <div className="r2-pricing__feature-list">
                {selectedPlan === "basic" ? (
                  <>
                    <PricingTierFeature
                      icon={
                        <DollarSign className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="$35.88 of included monthly credits"
                    />
                    <PricingTierFeature
                      icon={
                        <Gift className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="2 FREE credits every month"
                    />
                    <PricingTierFeature
                      icon={
                        <Coins className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="Use credits for any service"
                    />
                    <PricingTierFeature
                      icon={
                        <Tag className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="Automatic 15% off Bulk Credit purchases"
                    />
                    <PricingTierFeature
                      icon={
                        <Users className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="Perfect for small fleets getting started"
                    />
                    <PricingTierFeature
                      icon={
                        <TrendingUp className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="Predictable monthly spend"
                    />
                    <PricingTierFeature
                      icon={
                        <ArrowUpCircle className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="Upgrade or downgrade at any time"
                    />
                    <PricingTierFeature
                      icon={
                        <CheckCircle className="r2-pricing__icon r2-pricing__icon--pink" />
                      }
                      copy="No annual contract or commitments"
                    />
                  </>
                ) : (
                  <>
                    <PricingTierFeature
                      icon={
                        <DollarSign className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="$74.75 of included monthly credits"
                    />
                    <PricingTierFeature
                      icon={
                        <Gift className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="5 FREE credits every month"
                    />
                    <PricingTierFeature
                      icon={
                        <Coins className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="Use credits for any service"
                    />
                    <PricingTierFeature
                      icon={
                        <Tag className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="Automatic 20% off Bulk Credit purchases"
                    />
                    <PricingTierFeature
                      icon={
                        <Users className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="Best for fleets verifying renters monthly"
                    />
                    <PricingTierFeature
                      icon={
                        <TrendingUp className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="Predictable monthly spend for finance and ops"
                    />
                    <PricingTierFeature
                      icon={
                        <ArrowUpCircle className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="Upgrade or downgrade at any time"
                    />
                    <PricingTierFeature
                      icon={
                        <CheckCircle className="r2-pricing__icon r2-pricing__icon--blue" />
                      }
                      copy="No annual contract or commitments"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enterprise Card */}
        <div id="enterprise" className="r2-pricing__enterprise">
          <div className="r2-pricing__enterprise-card">
            <div className="r2-pricing__enterprise-layout">
              {/* Left side - Pricing info */}
              <div className="r2-pricing__enterprise-left">
                <div className="r2-pricing__enterprise-head">
                  <h3 className="r2-pricing__enterprise-title">Enterprise</h3>
                  <p className="r2-pricing__enterprise-subtitle">
                    Tailored verification solutions.
                  </p>
                </div>
                <div className="r2-pricing__enterprise-features">
                  <div className="r2-pricing__enterprise-feature">
                    <div className="r2-pricing__feature-icon">
                      <Workflow className="r2-pricing__icon r2-pricing__icon--sky" />
                    </div>
                    <span className="r2-pricing__feature-text">
                      Plug-In & Play: Drop effortless renter + insurance checks
                      into your workflow.
                    </span>
                  </div>
                  <div className="r2-pricing__enterprise-feature">
                    <div className="r2-pricing__feature-icon">
                      <ArrowUpCircle className="r2-pricing__icon r2-pricing__icon--blue" />
                    </div>
                    <span className="r2-pricing__feature-text">
                      Automated Compliance: Meet regulatory and underwriting
                      standards without the hassle.
                    </span>
                  </div>
                  <div className="r2-pricing__enterprise-feature">
                    <div className="r2-pricing__feature-icon">
                      <CheckCircle className="r2-pricing__icon r2-pricing__icon--blue" />
                    </div>
                    <span className="r2-pricing__feature-text">
                      Real-Time Decisions: Stop fraud, prevent losses, and
                      approve customers instantly.
                    </span>
                  </div>
                </div>
                <div className="r2-pricing__enterprise-cta">
                  <button
                    onClick={() => setIsContactSalesModalOpen(true)}
                    className="r2-pricing__cta-button r2-pricing__cta-button--full r2-pricing__cta-button--enterprise"
                  >
                    Contact Sales
                  </button>
                </div>
              </div>

              {/* Right side - Features in 2-column grid */}
              <div className="r2-pricing__enterprise-right">
                <AnimatedBeamMultiple />
              </div>
            </div>
          </div>
        </div>

        <div className="r2-pricing__fineprint">
          <div className="r2-pricing__fineprint-text">
            <sup>¹</sup> Rentid Inc.’s verification tools, including RentID™,
            provide risk indicators to supplement—not replace—your independent
            screening judgment. Results such as “pass,” “fail,” or other
            assessments do not constitute legal, insurance, financial, or
            compliance advice, nor do they guarantee a renter’s suitability or
            eliminate inherent risk of rental operations. By using these tools,
            you acknowledge that Rentid Inc. disclaims all responsibility and
            liability for any renter’s conduct, or any losses, claims, or
            damages arising from rentals or vehicle use. <sup>²</sup> Due to the
            nature of the services, there are no refunds, no warranty or
            expressed guarantees. <sup>³</sup> Subscription credits renew
            monthly, do not roll over, and are non-refundable.
            <sup>4</sup> Refunded and Bundle credits expire one year from the
            date of issuance.
            <sup>5</sup> Discounts apply to RentID service fees only; state DMV
            fees are billed separately.<sup>6</sup> Driver’s License Status
            checks are currently available only in the following states: CA, CT,
            DC, FL, GA, ID, KS, MN, MO, NE, NJ, ND, RI, TX, WA, WV, and WI.
          </div>
        </div>
      </div>
      <CreditModal
        isCreditsModalOpen={isCreditsModalOpen}
        setIsCreditsModalOpen={setIsCreditsModalOpen}
      />
      <ContactSalesModal
        open={isContactSalesModalOpen}
        onOpenChange={setIsContactSalesModalOpen}
      />
    </section>
  );
};

export default Pricing;
